import { COLOR_HEX, COLOR_HEX_DARK, type PaletteColor } from "@trip/domain/palette";

/**
 * A person, as a coloured initial.
 *
 * The colour is the one thing that makes two travellers tellable apart at a glance — the same
 * colour that draws their route on the map — so it is drawn the same way everywhere a person
 * appears: the media grid's people filter, a comment, the author of a photo, a row in the trip's
 * list of people.
 *
 * The hue is passed down as a custom property and the background is mixed from it in CSS rather
 * than listed as a second palette, so a tint can never drift from the colour it is a tint of.
 * Both themes are handed over at once because CSS, not JavaScript, is what knows which is on.
 */
export function Avatar({
  name,
  color,
  size,
}: {
  name: string;
  /** Null for someone whose colour is not known here; they get the neutral avatar. */
  color: PaletteColor | null;
  /** Smaller inside a comment or a chip than in a row of its own. */
  size?: "sm";
}) {
  const initial = name.trim()[0]?.toUpperCase() ?? "?";
  return (
    <span
      className={`avatar${size === "sm" ? " sm" : ""}`}
      data-colored={color !== null}
      style={
        color
          ? ({
              "--who": COLOR_HEX[color],
              "--who-dark": COLOR_HEX_DARK[color],
            } as React.CSSProperties)
          : undefined
      }
      aria-hidden="true"
    >
      {initial}
    </span>
  );
}
